import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '../icons/Icon.js';
import { cx } from '../utils/cx.js';
import { buttonClasses } from './Button.js';

/** 行の操作メニュー（ボタンに文字あり。禁止 #6 のとおりアイコンだけにしない） */
export interface MenuItem {
  label: ReactNode;
  onSelect: () => void;
  icon?: IconName;
  tone?: 'default' | 'danger';
  disabled?: boolean;
}

export function ActionMenu({ items, label = '操作', align = 'right', className }: { items: MenuItem[]; label?: ReactNode; align?: 'left' | 'right'; className?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const id = useId();
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={ref} className={cx('relative inline-block', className)}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-controls={id} onClick={() => setOpen((o) => !o)} className={buttonClasses('secondary', 'sm')}>
        <span>{label}</span>
        <Icon name="expand_more" size={20} />
      </button>
      {open ? (
        <ul id={id} role="menu" className={cx('absolute z-30 mt-1 min-w-48 rounded-input border border-line-strong bg-card py-1 shadow-elev-2', align === 'right' ? 'right-0' : 'left-0')}>
          {items.map((it, i) => (
            <li key={i} role="none">
              <button
                type="button"
                role="menuitem"
                disabled={it.disabled}
                onClick={() => {
                  setOpen(false);
                  it.onSelect();
                }}
                className={cx('flex min-h-12 w-full items-center gap-2 px-4 text-left text-body hover:bg-sunken disabled:disabled-look', it.tone === 'danger' && 'text-danger')}
              >
                {it.icon ? <Icon name={it.icon} size={20} /> : null}
                {it.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
